import type { ProductSearchTagGroup } from '@/lib/services/products/products.types'
import type { TaxonomyTag } from '@/lib/services/taxonomy-tags/taxonomy-tags.types'
import type { Dispatch, SetStateAction } from 'react'
import type { LucideIcon } from 'lucide-react'
import SearchInput from '@/components/form/SearchInput'
import { Check, ListChecks, Palette, Plus, Sprout, WandSparkles, X } from 'lucide-react'
import { useRef, useState } from 'react'
import { Badge, BadgeAction } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ToggleButton } from '@/components/ui/toggle-button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Typography } from '@/components/ui/typography'
type PickerTag = Pick<TaxonomyTag, 'id' | 'key' | 'label'> &
  Partial<Pick<TaxonomyTag, 'tag_type' | 'swatch_hex'>>
type PickerGroup = Omit<ProductSearchTagGroup, 'tags'> & {
  tags: PickerTag[]
  display_key?: string
  display_label?: string
}
type FlatTag = Omit<PickerTag, 'tag_type'> & { tag_type: string }
type IdList = readonly (number | string)[] | null | undefined

const tagTypeLabels: Partial<Record<string, string>> = {
  color: 'Colors',
  flower: 'Flowers',
  product_type: 'Product Type',
  material: 'Materials',
  season: 'Season',
  style: 'Style',
}

const additionalDetailTypes = ['style', 'material']

function normalizeIds(value: IdList) {
  return (Array.isArray(value) ? value : [])
    .map((id) => Number(id))
    .filter((id) => Number.isInteger(id) && id > 0)
}

function flattenTags(groups: PickerGroup[] | null | undefined): FlatTag[] {
  return (groups ?? []).flatMap((group) =>
    (group?.tags ?? []).map((tag) => ({
      ...tag,
      tag_type: tag?.tag_type ?? group?.tag_type,
    }))
  )
}

function orderedSelectedIds(selectedIds: IdList, tags: readonly Pick<PickerTag, 'id'>[]) {
  const normalizedSelectedIds = normalizeIds(selectedIds)
  const selectedSet = new Set(normalizedSelectedIds)
  const orderedKnownIds = tags
    .map((tag) => Number(tag.id))
    .filter((id) => selectedSet.has(id))
    .filter((id, index, ids) => ids.indexOf(id) === index)
  const knownIdSet = new Set(orderedKnownIds)
  const unavailableIds = normalizedSelectedIds
    .filter((id) => !knownIdSet.has(id))
    .filter((id, index, ids) => ids.indexOf(id) === index)

  return [...orderedKnownIds, ...unavailableIds]
}

function getGroupLabel(group: PickerGroup) {
  if (group?.display_label) {
    return group.display_label
  }

  return tagTypeLabels[group?.tag_type] ?? group?.tag_type ?? 'Details'
}

function ColorSwatch({ color, label }: { color?: string | null; label: string }) {
  if (!color) {
    return null
  }

  return (
    <span
      aria-hidden="true"
      title={label}
      className="size-3.5 shrink-0 rounded-full border border-border shadow-sm"
      style={{ backgroundColor: color }}
    />
  )
}

function filterGroupsBySearch(groups: PickerGroup[], search: string) {
  const normalizedSearch = search.trim().toLowerCase()

  return (groups ?? [])
    .map((group) => ({
      ...group,
      tags: (group?.tags ?? []).filter((tag) => {
        if (!normalizedSearch) {
          return true
        }

        return `${tag?.label ?? ''} ${tag?.key ?? ''}`.toLowerCase().includes(normalizedSearch)
      }),
    }))
    .filter((group) => group.tags.length > 0)
}

function TagChecklist({
  disabled = false,
  groups = [],
  selectedIdSet,
  suggestedIdSet = new Set(),
  onToggle,
}: {
  disabled?: boolean
  groups?: PickerGroup[]
  selectedIdSet: Set<number>
  suggestedIdSet?: Set<number>
  onToggle: (id: number, selected: boolean) => void
}) {
  return (
    <div className="space-y-7 p-4">
      {groups.map((group) => (
        <section key={group.display_key ?? group.tag_type} className="space-y-3.5">
          <Typography variant="labelStrong" className="block">
            {getGroupLabel(group)}
          </Typography>
          <div className="flex flex-wrap gap-2">
            {group.tags.map((tag) => {
              const tagId = Number(tag.id)
              const isSelected = selectedIdSet.has(tagId)
              const isSuggested = suggestedIdSet.has(tagId)

              return (
                <ToggleButton
                  key={tag.id}
                  type="button"
                  pressed={isSelected}
                  disabled={disabled}
                  onClick={() => onToggle(tagId, !isSelected)}
                  size="xs"
                  className="h-auto min-h-9 gap-2 px-3.5 py-2 has-[>svg]:px-3.5"
                >
                  {isSelected ? (
                    <Check className="size-3.5 shrink-0" aria-hidden="true" />
                  ) : (
                    <Plus className="size-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
                  )}
                  <ColorSwatch color={tag.swatch_hex} label={tag.label} />
                  <span>{tag.label}</span>
                  {isSuggested ? (
                    <Badge aria-hidden="true" variant="secondary" size="micro">
                      Suggested
                    </Badge>
                  ) : null}
                </ToggleButton>
              )
            })}
          </div>
        </section>
      ))}
    </div>
  )
}

function DetailPill({
  disabled = false,
  onRemove = null,
  tag,
}: {
  disabled?: boolean
  onRemove?: ((tag: FlatTag) => void) | null
  tag: FlatTag
}) {
  return (
    <Badge variant="brand" size="sm" className="gap-2 px-3.5 py-1.5">
      <ColorSwatch color={tag.swatch_hex} label={tag.label} />
      <span>{tag.label}</span>
      {onRemove ? (
        <BadgeAction
          className="-mr-1"
          aria-label={`Remove ${tag.label}`}
          disabled={disabled}
          onClick={() => onRemove(tag)}
        >
          <X className="size-3" aria-hidden="true" />
        </BadgeAction>
      ) : null}
    </Badge>
  )
}

function SearchDetailSummarySection({
  description,
  disabled = false,
  icon: Icon,
  label,
  onRemoveTag = null,
  tags = [],
}: {
  description: string
  disabled?: boolean
  icon: LucideIcon
  label: string
  onRemoveTag?: ((tag: FlatTag) => void) | null
  tags?: FlatTag[]
}) {
  return (
    <section className="min-w-0 space-y-2 py-1">
      <div className="flex items-start gap-2.5">
        <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center text-interaction">
          <Icon className="size-4" />
        </span>
        <div className="min-w-0">
          <Typography variant="labelStrong" className="block">
            {label}
          </Typography>
          <Typography variant="supporting" className="block">
            {description}
          </Typography>
        </div>
      </div>
      {tags.length > 0 ? (
        <div className="flex flex-wrap gap-2 pl-7">
          {tags.map((tag) => (
            <DetailPill key={tag.id} disabled={disabled} onRemove={onRemoveTag} tag={tag} />
          ))}
        </div>
      ) : (
        <div className="pl-7">
          <Badge variant="placeholder" size="placeholder">
            Nothing selected yet.
          </Badge>
        </div>
      )}
    </section>
  )
}

export default function ProductSearchTagPicker({
  id,
  disabled = false,
  groups = [],
  value = [],
  suggestionGroups = groups,
  suggestionValue = value,
  isApplyingSuggestions = false,
  isSuggesting = false,
  onApplySuggestions = null,
  onSuggest = null,
  onChange,
}: {
  id: string
  disabled?: boolean
  groups?: PickerGroup[]
  value?: number[]
  suggestionGroups?: PickerGroup[]
  suggestionValue?: number[]
  isApplyingSuggestions?: boolean
  isSuggesting?: boolean
  onApplySuggestions?: ((ids: number[]) => void | Promise<void>) | null
  onSuggest?: (() => number[] | null | undefined | Promise<number[] | null | undefined>) | null
  onChange?: (ids: number[]) => void
}) {
  const allTags = flattenTags(groups)
  const allSuggestionTags = flattenTags(suggestionGroups)
  const latestValueRef = useRef(value)
  const latestAllTagsRef = useRef(allTags)
  // oxlint-disable-next-line react/refs -- grandfathered
  latestValueRef.current = suggestionValue
  // oxlint-disable-next-line react/refs -- grandfathered
  latestAllTagsRef.current = allSuggestionTags
  const selectedIds = orderedSelectedIds(value, allTags)
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const selectedIdSet = new Set(selectedIds)
  const selectedTags = allTags.filter((tag) => selectedIdSet.has(Number(tag.id)))
  const selectedTagsByType = selectedTags.reduce<Record<string, FlatTag[]>>((tagsByType, tag) => {
    const tagsForType = (tagsByType[tag.tag_type] ??= [])
    tagsForType.push(tag)

    return tagsByType
  }, {})
  const additionalDetailTags = additionalDetailTypes.flatMap(
    (type) => selectedTagsByType[type] ?? []
  )
  const [draftIds, setDraftIds] = useState(() => selectedIds)
  const [suggestionOpen, setSuggestionOpen] = useState(false)
  const [suggestionDraftIds, setSuggestionDraftIds] = useState<number[]>([])
  const [suggestionSearch, setSuggestionSearch] = useState('')
  const [suggestedIds, setSuggestedIds] = useState<number[]>([])
  const suggestedIdSet = new Set(suggestedIds)
  const filteredGroups = filterGroupsBySearch(groups, search)
  const filteredSuggestionGroups = filterGroupsBySearch(suggestionGroups, suggestionSearch)
  const draftIdsInOrder = orderedSelectedIds(draftIds, allTags)
  const suggestionDraftIdsInOrder = orderedSelectedIds(suggestionDraftIds, allSuggestionTags)
  const draftIdSet = new Set(draftIdsInOrder)
  const suggestionDraftIdSet = new Set(suggestionDraftIdsInOrder)
  const hasAvailableGroups = groups.length > 0

  const resetDraft = () => {
    setDraftIds(selectedIds)
    setSearch('')
  }

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      resetDraft()
    }

    setOpen(nextOpen)
  }

  const setTagInDraft = (
    setIds: Dispatch<SetStateAction<number[]>>,
    tagId: number,
    isSelected: boolean,
    availableTags = allTags
  ) => {
    setIds((currentIds) => {
      const nextIds = new Set(normalizeIds(currentIds))
      const normalizedTagId = Number(tagId)

      if (isSelected) {
        nextIds.add(normalizedTagId)
      } else {
        nextIds.delete(normalizedTagId)
      }

      return orderedSelectedIds([...nextIds], availableTags)
    })
  }

  const setDraftTag = (tagId: number, isSelected: boolean) =>
    setTagInDraft(setDraftIds, tagId, isSelected)

  const setSuggestionDraftTag = (tagId: number, isSelected: boolean) =>
    setTagInDraft(setSuggestionDraftIds, tagId, isSelected, allSuggestionTags)

  const removeSelectedTag = (tag: FlatTag) => {
    const tagId = Number(tag?.id)

    if (!Number.isInteger(tagId) || tagId <= 0) {
      return
    }

    onChange?.(selectedIds.filter((selectedId) => selectedId !== tagId))
  }

  const applyDraft = () => {
    onChange?.(draftIdsInOrder)
    setOpen(false)
  }

  const reviewSuggestedTags = async () => {
    const recommendationIds = await onSuggest?.()

    if (!Array.isArray(recommendationIds)) {
      return
    }

    const latestAllTags = latestAllTagsRef.current
    const nextSuggestedIds = orderedSelectedIds(recommendationIds, latestAllTags)
    const latestSelectedIds = orderedSelectedIds(latestValueRef.current, latestAllTags)

    setSuggestedIds(nextSuggestedIds)
    setSuggestionDraftIds(
      orderedSelectedIds([...latestSelectedIds, ...nextSuggestedIds], latestAllTags)
    )
    setSuggestionSearch('')
    setSuggestionOpen(true)
  }

  const applySuggestionDraft = async () => {
    try {
      if (onApplySuggestions) {
        await onApplySuggestions(suggestionDraftIdsInOrder)
      } else {
        onChange?.(suggestionDraftIdsInOrder)
      }
    } catch {
      return
    }

    setSuggestionOpen(false)
  }

  return (
    <div className="@container relative">
      <div className="mb-3 flex flex-wrap items-center justify-end gap-2 @lg:absolute @lg:right-0 @lg:-top-[3.25rem] @lg:mb-0">
        <Dialog open={open} onOpenChange={handleOpenChange}>
          <DialogTrigger asChild>
            <Button
              id={id}
              type="button"
              variant="outline"
              size="sm"
              className="shrink-0 px-3.5"
              disabled={disabled || !hasAvailableGroups}
              onClick={() => handleOpenChange(true)}
            >
              <ListChecks className="size-4" />
              Adjust Details
            </Button>
          </DialogTrigger>
          {open ? (
            <DialogContent size="xlarge" viewport="contained">
              <DialogHeader>
                <DialogTitle>Choose Customer Search Details</DialogTitle>
                <DialogDescription>
                  Pick the details that help shoppers narrow down products on your website.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-3">
                <SearchInput
                  value={search}
                  setValue={setSearch}
                  placeholder="Search details..."
                  className="max-w-md"
                />
                <div className="flex items-center justify-between gap-3">
                  <Typography variant="label" tone="muted">
                    {draftIdsInOrder.length} detail
                    {draftIdsInOrder.length === 1 ? '' : 's'} selected
                  </Typography>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={disabled || draftIdsInOrder.length === 0}
                    onClick={() => setDraftIds([])}
                  >
                    Clear all
                  </Button>
                </div>
              </div>

              <div className="rounded-xl border border-border/60 bg-background">
                <ScrollArea className="h-[min(560px,calc(100dvh-20rem))]">
                  {filteredGroups.length > 0 ? (
                    <TagChecklist
                      disabled={disabled}
                      groups={filteredGroups}
                      selectedIdSet={draftIdSet}
                      onToggle={setDraftTag}
                    />
                  ) : (
                    <div className="flex min-h-[240px] items-center justify-center p-6 text-center">
                      <Typography variant="supporting">No details matched that search.</Typography>
                    </div>
                  )}
                </ScrollArea>
              </div>

              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                  Cancel
                </Button>
                <Button type="button" disabled={disabled} onClick={applyDraft}>
                  <Check className="size-4" />
                  Apply Details
                </Button>
              </div>
            </DialogContent>
          ) : null}
        </Dialog>
        {onSuggest ? (
          <Button
            type="button"
            variant="default"
            size="sm"
            className="shrink-0 px-3.5"
            disabled={disabled || isSuggesting || isApplyingSuggestions || !hasAvailableGroups}
            onClick={reviewSuggestedTags}
          >
            <WandSparkles className="size-4" />
            {isSuggesting ? 'Suggesting...' : 'Suggest Details'}
          </Button>
        ) : null}
      </div>

      <div className="grid border-t border-border/60 pt-4 @2xl:grid-cols-2 @2xl:gap-0">
        <div className="space-y-5 @2xl:pr-8">
          <SearchDetailSummarySection
            disabled={disabled}
            icon={ListChecks}
            label="Product Type"
            onRemoveTag={removeSelectedTag}
            description="The product bucket shoppers would naturally look for."
            tags={selectedTagsByType.product_type ?? []}
          />
          <div className="border-t border-border/60" />
          <SearchDetailSummarySection
            disabled={disabled}
            icon={Palette}
            label="Colors"
            onRemoveTag={removeSelectedTag}
            description="The color families customers may have in mind."
            tags={selectedTagsByType.color ?? []}
          />
        </div>
        <div className="mt-5 space-y-5 border-t border-border/60 pt-5 @2xl:mt-0 @2xl:border-t-0 @2xl:border-l @2xl:pt-0 @2xl:pl-8">
          <SearchDetailSummarySection
            disabled={disabled}
            icon={Sprout}
            label="Flowers"
            onRemoveTag={removeSelectedTag}
            description="Recognizable blooms used in this product."
            tags={selectedTagsByType.flower ?? []}
          />
          <div className="border-t border-border/60" />
          <SearchDetailSummarySection
            disabled={disabled}
            icon={ListChecks}
            label="Additional Details"
            onRemoveTag={removeSelectedTag}
            description="Style or material when it helps shoppers."
            tags={additionalDetailTags}
          />
        </div>
      </div>

      <Dialog open={suggestionOpen} onOpenChange={setSuggestionOpen}>
        {suggestionOpen ? (
          <DialogContent size="xlarge" viewport="contained">
            <DialogHeader>
              <DialogTitle>Review Suggested Details</DialogTitle>
              <DialogDescription>
                These are based on the product name, description, and variant details. Adjust
                anything that does not fit.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3">
              <SearchInput
                value={suggestionSearch}
                setValue={setSuggestionSearch}
                placeholder="Search details..."
                className="max-w-md"
              />
              <div className="flex items-center justify-between gap-3">
                <Typography variant="label" tone="muted">
                  {suggestionDraftIdsInOrder.length} detail
                  {suggestionDraftIdsInOrder.length === 1 ? '' : 's'} selected
                </Typography>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={
                    disabled || isApplyingSuggestions || suggestionDraftIdsInOrder.length === 0
                  }
                  onClick={() => setSuggestionDraftIds([])}
                >
                  Clear all
                </Button>
              </div>
            </div>

            <div className="rounded-xl border border-border/60 bg-background">
              <ScrollArea className="h-[min(560px,calc(100dvh-20rem))]">
                {filteredSuggestionGroups.length > 0 ? (
                  <TagChecklist
                    disabled={disabled || isApplyingSuggestions}
                    groups={filteredSuggestionGroups}
                    selectedIdSet={suggestionDraftIdSet}
                    suggestedIdSet={suggestedIdSet}
                    onToggle={setSuggestionDraftTag}
                  />
                ) : (
                  <div className="flex min-h-[240px] items-center justify-center p-6 text-center">
                    <Typography variant="supporting">No details matched that search.</Typography>
                  </div>
                )}
              </ScrollArea>
            </div>

            {suggestedIds.length === 0 ? (
              <Typography variant="supporting">
                No new details were suggested from the product text.
              </Typography>
            ) : null}

            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                disabled={isApplyingSuggestions}
                onClick={() => setSuggestionOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                disabled={disabled || isApplyingSuggestions}
                onClick={applySuggestionDraft}
              >
                <Check className="size-4" />
                {isApplyingSuggestions ? 'Applying...' : 'Apply Suggested Details'}
              </Button>
            </div>
          </DialogContent>
        ) : null}
      </Dialog>
    </div>
  )
}
